import { useState, useEffect, useRef, useCallback } from 'react';
import { useSession } from '../../context/SessionContext';
import { getQuiz } from '../../services/quizService';
import { startSession, nextQuestion, endSession } from '../../services/sessionService';
import useAudioManager from '../../hooks/useAudioManager';
import HostLobby from './HostLobby';
import Leaderboard from './Leaderboard';
import QuestionResults from './QuestionResults';
import FinalPodium from './FinalPodium';

const AUTO_ADVANCE_RESULTS_DELAY = 8; // sekund på resultatskjerm
const AUTO_ADVANCE_LEADERBOARD_DELAY = 5; // sekund på resultatliste

export default function SessionControl({ sessionId, onBack }) {
  const { session, loading, error } = useSession();
  const [quiz, setQuiz] = useState(null);
  const [quizLoading, setQuizLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [hostView, setHostView] = useState('question'); // 'question' or 'leaderboard'
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [autoAdvanceCountdown, setAutoAdvanceCountdown] = useState(null);

  const autoAdvanceTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const countdownDelayRef = useRef(null);

  // Audio manager for background music
  const audio = useAudioManager();

  // Fetch quiz data when session is loaded
  useEffect(() => {
    if (session && session.quizId) {
      setQuizLoading(true);
      getQuiz(session.quizId)
        .then((quizData) => {
          setQuiz(quizData);
          setQuizLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching quiz:', err);
          setQuizLoading(false);
        });
    }
  }, [session?.quizId]);

  // Reset view when question changes
  useEffect(() => {
    setHostView('question');
  }, [session?.currentQuestionIndex]);

  // --- Timer helpers ---
  const clearAutoAdvanceTimers = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setAutoAdvanceCountdown(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearAutoAdvanceTimers();
  }, [clearAutoAdvanceTimers]);

  // --- Derived state ---
  const participantsList = session?.participants
    ? Object.values(session.participants)
    : [];

  const currentQuestion = quiz?.questions?.[session?.currentQuestionIndex];
  const isLastQuestion = session?.currentQuestionIndex === (quiz?.questions?.length - 1);
  const totalQuestions = quiz?.questions?.length || 0;

  // True when every joined participant has submitted an answer for the
  // current question. Used to reveal results as soon as the room is done.
  const allAnswered =
    !!currentQuestion &&
    participantsList.length > 0 &&
    participantsList.every((p) => p.answers?.[currentQuestion.id] !== undefined);

  // --- Handlers ---
  const handleStartSession = async () => {
    setActionLoading(true);
    try {
      await startSession(sessionId);
    } catch (err) {
      console.error('Error starting session:', err);
      alert('Klarte ikkje starte økta: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleNextQuestion = useCallback(async () => {
    clearAutoAdvanceTimers();
    setActionLoading(true);
    try {
      await nextQuestion(sessionId);
      setHostView('question');
    } catch (err) {
      console.error('Error advancing question:', err);
      alert('Klarte ikkje gå til neste spørsmål: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  }, [sessionId, clearAutoAdvanceTimers]);

  const handleEndSession = useCallback(async (skipConfirm = false) => {
    if (!skipConfirm && !window.confirm('Er du sikker på at du vil avslutte kvissen?')) return;

    clearAutoAdvanceTimers();
    setActionLoading(true);
    try {
      await endSession(sessionId);
    } catch (err) {
      console.error('Error ending session:', err);
      alert('Klarte ikkje avslutte økta: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  }, [sessionId, clearAutoAdvanceTimers]);

  const handleShowLeaderboard = useCallback(() => {
    clearAutoAdvanceTimers();
    setHostView('leaderboard');
  }, [clearAutoAdvanceTimers]);

  const handleBack = () => {
    onBack?.();
  };

  const handlePlayAgain = () => {
    onBack?.();
  };

  const handleExitSession = async () => {
    if (window.confirm('Er du sikker på at du vil avslutte økta og gå tilbake?')) {
      clearAutoAdvanceTimers();
      try {
        await endSession(sessionId);
      } catch (err) {
        console.error('Error ending session:', err);
      }
      onBack?.();
    }
  };

  // --- Auto-advance: question results → leaderboard ---
  useEffect(() => {
    if (!autoAdvance || hostView !== 'question' || session?.status !== 'active' || !session?.questionStartTime || !currentQuestion) {
      return;
    }

    const timeLimit = currentQuestion.timeLimit || 30;
    const startTime = new Date(session.questionStartTime).getTime();
    const expiryTime = startTime + (timeLimit * 1000);
    const now = Date.now();
    // Reveal as soon as either timer expires or every participant has answered
    const waitMs = allAnswered ? 0 : Math.max(0, expiryTime - now);

    // Wait for reveal condition, then start results countdown
    const waitTimer = setTimeout(() => {
      const delay = AUTO_ADVANCE_RESULTS_DELAY;
      setAutoAdvanceCountdown(delay);

      countdownIntervalRef.current = setInterval(() => {
        setAutoAdvanceCountdown(prev => {
          if (prev === null || prev <= 1) return 0;
          return prev - 1;
        });
      }, 1000);

      autoAdvanceTimerRef.current = setTimeout(() => {
        handleShowLeaderboard();
      }, delay * 1000);
    }, waitMs);

    return () => {
      clearTimeout(waitTimer);
      clearAutoAdvanceTimers();
    };
  }, [autoAdvance, hostView, session?.status, session?.questionStartTime, currentQuestion, allAnswered, handleShowLeaderboard, clearAutoAdvanceTimers]);

  // --- Auto-advance: leaderboard → next question ---
  useEffect(() => {
    if (!autoAdvance || hostView !== 'leaderboard' || session?.status !== 'active') {
      return;
    }

    const delay = AUTO_ADVANCE_LEADERBOARD_DELAY;
    setAutoAdvanceCountdown(delay);

    countdownIntervalRef.current = setInterval(() => {
      setAutoAdvanceCountdown(prev => {
        if (prev === null || prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    autoAdvanceTimerRef.current = setTimeout(() => {
      if (isLastQuestion) {
        handleEndSession(true); // skip confirm for auto-advance
      } else {
        handleNextQuestion();
      }
    }, delay * 1000);

    return () => clearAutoAdvanceTimers();
  }, [autoAdvance, hostView, session?.status, isLastQuestion, handleNextQuestion, handleEndSession, clearAutoAdvanceTimers]);

  // --- Audio: lobby music ---
  useEffect(() => {
    if (session?.status === 'waiting') {
      audio.play('lobby', { loop: true, volume: 0.4 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status]);

  // --- Audio: countdown music (synced to question timer) ---
  useEffect(() => {
    if (session?.status !== 'active' || hostView !== 'question' || !session?.questionStartTime || !currentQuestion) {
      return;
    }

    const MUSIC_DURATION = 61; // Mikkelbyks kort versjon is ~61 seconds
    const timeLimit = currentQuestion.timeLimit || 20;
    const startTime = new Date(session.questionStartTime).getTime();
    const now = Date.now();

    // Calculate delay so music ends when timer expires
    const delayFromQuestionStart = Math.max(0, (timeLimit - MUSIC_DURATION)) * 1000;
    const elapsed = now - startTime;
    const delayMs = Math.max(0, delayFromQuestionStart - elapsed);

    // Calculate offset for short timers (< 20s)
    const startOffset = Math.max(0, MUSIC_DURATION - timeLimit);

    countdownDelayRef.current = setTimeout(() => {
      audio.fadeOut(500, () => {
        audio.play('countdown', { loop: false, volume: 0.5, startOffset });
      });
    }, delayMs);

    return () => {
      if (countdownDelayRef.current) {
        clearTimeout(countdownDelayRef.current);
        countdownDelayRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status, session?.questionStartTime, hostView]);

  // --- Audio: stop on leaderboard ---
  useEffect(() => {
    if (hostView === 'leaderboard') {
      audio.stopAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostView]);

  // --- Audio: podium music ---
  useEffect(() => {
    if (session?.status === 'finished') {
      audio.stopAll();
      audio.play('podium', { loop: true, volume: 0.5 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status]);

  // --- Answer calculation helpers ---
  const getAnswerCounts = () => {
    const answerCount = currentQuestion?.answers?.length || 4;
    if (!currentQuestion || !participantsList.length) return new Array(answerCount).fill(0);

    const questionId = currentQuestion?.id;
    const counts = new Array(answerCount).fill(0);

    participantsList.forEach(participant => {
      const answerData = participant.answers?.[questionId];
      if (answerData && answerData.answerId) {
        const answerIndex = currentQuestion.answers.findIndex(a => a.id === answerData.answerId);
        if (answerIndex >= 0 && answerIndex < answerCount) {
          counts[answerIndex]++;
        }
      }
    });

    return counts;
  };

  const getCorrectAnswerIndex = () => {
    if (!currentQuestion?.answers) return 0;
    return currentQuestion.answers.findIndex(a => a.isCorrect);
  };

  const getTotalAnswers = () => {
    const questionId = currentQuestion?.id;
    return participantsList.filter(p => p.answers?.[questionId] !== undefined).length;
  };

  // --- Render ---
  if (loading || quizLoading) {
    return (
      <div className="min-h-screen bg-mp-bg-lightgreen flex items-center justify-center font-body">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mp-magenta mx-auto mb-4"></div>
          <p className="font-display text-mp-darkgreen text-xl">Lastar økt …</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-mp-bg-lightgreen flex items-center justify-center p-4 font-body">
        <div className="bg-white border border-[rgba(163,0,97,0.20)] rounded-2xl p-8 max-w-md shadow-mp-1">
          <h2 className="font-display text-2xl text-mp-magenta mb-2">Feil</h2>
          <p className="font-body text-mp-stone-700">{error}</p>
        </div>
      </div>
    );
  }

  // Waiting State - Show HostLobby
  if (session?.status === 'waiting') {
    return (
      <HostLobby
        sessionCode={sessionId}
        participants={session.participants}
        onStartGame={handleStartSession}
        onBack={handleBack}
        autoAdvance={autoAdvance}
        onAutoAdvanceChange={setAutoAdvance}
        isMuted={audio.isMuted}
        onToggleMute={audio.toggleMute}
      />
    );
  }

  // Active State
  if (session?.status === 'active') {
    // Show Leaderboard view
    if (hostView === 'leaderboard') {
      return (
        <Leaderboard
          participants={participantsList}
          currentRound={(session?.currentQuestionIndex || 0) + 1}
          totalRounds={totalQuestions}
          onNextQuestion={isLastQuestion ? handleEndSession : handleNextQuestion}
          onBack={() => { clearAutoAdvanceTimers(); setHostView('question'); }}
          onExit={handleExitSession}
          autoAdvanceCountdown={autoAdvance ? autoAdvanceCountdown : null}
        />
      );
    }

    // Show Question Results view (default during active game)
    return (
      <QuestionResults
        question={currentQuestion}
        questionNumber={(session?.currentQuestionIndex || 0) + 1}
        totalQuestions={totalQuestions}
        answerCounts={getAnswerCounts()}
        totalAnswers={getTotalAnswers()}
        correctAnswerIndex={getCorrectAnswerIndex()}
        questionStartTime={session?.questionStartTime}
        questionDuration={currentQuestion?.timeLimit || 30}
        sessionCode={sessionId}
        onNext={handleShowLeaderboard}
        onEndGame={handleEndSession}
        onBack={handleBack}
        onExit={handleExitSession}
        autoAdvanceCountdown={autoAdvance ? autoAdvanceCountdown : null}
        allAnswered={allAnswered}
      />
    );
  }

  // Finished State - Show FinalPodium
  if (session?.status === 'finished') {
    return (
      <FinalPodium
        participants={participantsList}
        onPlayAgain={handlePlayAgain}
        onBackToDashboard={handleBack}
        isMuted={audio.isMuted}
        onToggleMute={audio.toggleMute}
      />
    );
  }

  return null;
}
